/**
 * Image format capability detection.
 *
 * The server picks AVIF or WebP from an `Accept` header. That works for `<img>`
 * requests, which the browser annotates with `image/avif,image/webp,*` — but
 * the gallery asks for its URLs over `fetch`, and a fetch for JSON sends
 * a wildcard Accept. Negotiating from *that* header silently downgrades everyone to WebP,
 * costing roughly 30% more bytes on every thumbnail in the product.
 *
 * So the client detects support once and tells the server explicitly, using the
 * `accept` field the API already accepts.
 */

/**
 * A 1×1 red AVIF, 295 bytes, produced by the same libheif/libaom build that
 * encodes our thumbnails (`sharp(...).avif()`). Generated rather than copied
 * from a blog post: a subtly malformed probe fails to decode in browsers that
 * *do* support AVIF, silently downgrading every user to WebP — which is
 * exactly the bug this module exists to prevent.
 */
const AVIF_PROBE =
  "data:image/avif;base64,AAAAHGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZgAAAOptZXRhAAAAAAAAACFoZGxyAAAAAAAAAABwaWN0AAAAAAAAAAAAAAAAAAAAAA5waXRtAAAAAAABAAAAImlsb2MAAAAAREAAAQABAAAAAAEOAAEAAAAAAAAAGQAAACNpaW5mAAAAAAABAAAAFWluZmUCAAAAAAEAAGF2MDEAAAAAamlwcnAAAABLaXBjbwAAABNjb2xybmNseAABAA0ABoAAAAAMYXYxQ4EgAgAAAAAUaXNwZQAAAAAAAAABAAAAAQAAABBwaXhpAAAAAAMICAgAAAAXaXBtYQAAAAAAAAABAAEEAYIDBAAAACFtZGF0EgAKBzgABhAQ0GkyDB+QP///xAAArLK+CQ==";

let cached: Promise<string> | null = null;

async function supportsAvif(): Promise<boolean> {
  if (typeof createImageBitmap !== "function") return false;
  try {
    const response = await fetch(AVIF_PROBE);
    const blob = await response.blob();
    const bitmap = await createImageBitmap(blob);
    bitmap.close?.();
    return true;
  } catch {
    return false;
  }
}

/**
 * An Accept-style string describing what this browser can actually render.
 * Detected once per session and reused — the answer cannot change mid-session.
 */
export function imageAccept(): Promise<string> {
  cached ??= supportsAvif().then((avif) =>
    avif ? "image/avif,image/webp,image/jpeg" : "image/webp,image/jpeg",
  );
  return cached;
}

/**
 * Synchronous best guess for render paths that cannot await. Returns the
 * resolved value once detection has completed, and the safe subset before that.
 */
let resolved: string | null = null;
void imageAccept().then((value) => {
  resolved = value;
});

export function imageAcceptSync(): string {
  return resolved ?? "image/webp,image/jpeg";
}
